import * as types from 'constants/ActionTypes';
import {
  BRANDS_FILTER_URL,
  CRON_JOBS_REFRESH_TIME_URL,
  INTERVENTIONS_STATISTICS_REDUCE_URL,
  SELF_SERVICE_ADVICE_STATISTICS_REDUCE_URL,
  SELF_SERVICE_PROACTIVE_WORKS_STATISTICS_REDUCE_URL,
  SELF_SERVICE_STATISTICS_REDUCE_URL,
  SELF_SERVICE_STATISTICS_TOP_CONTRIBUTORS_URL,
  TOP_CONTRIBUTORS_STATISTICS_URL,
} from 'constants/ApiConstants';
import { MAX_RECORD, PaginationType } from 'constants/Global';
import { INTERVENTION_MODE, ANSWERED, CLOSED } from 'constants/InterventionConstants';
import { PARTS } from 'constants/ModeConstants';
import { SELF_SERVICE_STATS_REQUEST_TYPES } from 'constants/SelfServiceConstants';

import {
  CHAT_STATISTICS_URL_BY_MODE,
  DATE_FIELDS,
  DAY,
  YEAR,
  ExportTypeEnum,
  STATS_STATUS,
  STATISTICS_URL,
  getStatsModules,
  STATS_STATUS_BEFORE_CLOSE,
} from 'constants/StatisticsConstants';

import { MediaTypeEnum } from 'modules/global/typing/enums/media.type.enum';

import { camelCase } from 'utils/global';

import { DETAILS_SELF_SERVICE_STATS_BY_TYPES, getGroupByFilters } from './actions.utils';
import * as actionTypes from './actionTypes';

/**
 * @param {import('react-router').Location & { isFirstRendering: boolean }} payload
 * @param {Object} meta
 */
export const locationChange = (payload, meta) => ({
  type: actionTypes.STATISTICS_LOCATION_CHANGE,
  payload,
  meta,
});

/**
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchRefreshTimeAction}
 */
export const fetchRefreshTime = (meta = {}) => ({
  type: types.FETCH_LATEST_REFRESH_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: CRON_JOBS_REFRESH_TIME_URL,
    },
  },
  meta,
});

/**
 * @param {string} timestamp
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchRefreshTimeSuccessAction}
 */
export const fetchRefreshTimeSuccess = (timestamp, meta = {}) => ({
  type: types.FETCH_LATEST_REFRESH_SUCCESS,
  payload: {
    timestamp,
  },
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchRefreshTimeFailureAction}
 */
export const fetchRefreshTimeFailure = (error, meta = {}) => ({
  type: types.FETCH_LATEST_REFRESH_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {import('modules/statistics/types').ReducerParams} params
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchInterventionStatusStatsRequestAction}
 */
export const fetchInterventionStatusStatsRequest = (params, meta = {}) => ({
  type: types.FETCH_STATISTICS_INTERVENTION_STATUS_STATS_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: `${STATISTICS_URL[params.mode]}/reduce`,
      params: {
        ...params,
        size: MAX_RECORD,
        status: STATS_STATUS,
        statusBeforeClose: STATS_STATUS_BEFORE_CLOSE,
        ...(params.sentFrom ? { exist: 'sentOn' } : {}),
        reducer: ['status,none', 'statusBeforeClose,none', 'id,count', 'acceptedOn,count', 'rejectedOn,count'],
      },
    },
  },
  meta,
});

/**
 * @param {import('modules/global/generic.types').PageContent<import('modules/statistics/types').InterventionStatusReducer>} data
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchInterventionStatusStatsSuccessAction}
 */
export const fetchInterventionStatusStatsSuccess = (data, meta = {}) => ({
  type: types.FETCH_STATISTICS_INTERVENTION_STATUS_STATS_SUCCESS,
  payload: data,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchInterventionStatusStatsFailureAction}
 */
export const fetchInterventionStatusStatsFailure = (error, meta = {}) => ({
  type: types.FETCH_STATISTICS_INTERVENTION_STATUS_STATS_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {import('modules/statistics/types').ReducerParams} params
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchInterventionRatingStatsRequestAction}
 */
export const fetchInterventionRatingStatsRequest = (params, meta) => ({
  type: types.FETCH_STATISTICS_INTERVENTION_RATING_STATS_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: `${STATISTICS_URL[params.mode]}/reduce`,
      params: {
        ...params,
        size: MAX_RECORD,
        reducer: ['id,count', 'rating,none'],
        status: STATS_STATUS,
        statusBeforeClose: STATS_STATUS_BEFORE_CLOSE,
        exist: ['rating', ...(params.sentFrom ? ['sentOn'] : [])],
      },
    },
  },
  meta,
});

/**
 * @param {import('modules/global/generic.types').PageContent<import('modules/statistics/types').InterventionRatingReducer>} data
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchInterventionRatingStatsSuccessAction}
 */
export const fetchInterventionRatingStatsSuccess = (data, meta = {}) => ({
  type: types.FETCH_STATISTICS_INTERVENTION_RATING_STATS_SUCCESS,
  payload: data,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchInterventionRatingStatsFailureAction}
 */
export const fetchInterventionRatingStatsFailure = (error, meta = {}) => ({
  type: types.FETCH_STATISTICS_INTERVENTION_RATING_STATS_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {import('modules/statistics/types').ReducerParams} params
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchInterventionFollowUpStatsRequestAction}
 */
export const fetchFollowUpStatsRequest = (params, meta) => ({
  type: types.FETCH_STATISTICS_INTERVENTION_FOLLOW_UP_STATS_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: `${STATISTICS_URL[params.mode]}/reduce`,
      params: {
        ...params,
        reducer: ['hasFollowUpSent,sum', 'sent,sum', 'hasRatingComment,sum'],
        status: STATS_STATUS,
        statusBeforeClose: STATS_STATUS_BEFORE_CLOSE,
        exist: params.sentFrom ? ['sentOn'] : [],
      },
    },
  },
  meta: {
    '@app-request': true,
    ...meta,
  },
});

/**
 * @param {'day' | 'week' | 'month'} groupedBy
 * @param {import('modules/statistics/types').ReducerParams} params
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchTimeLineStatusStatsRequestAction}
 */
export const fetchTimeLineStatusStatsRequest = (groupedBy, params, meta = {}) => {
  const groupBy = groupedBy?.toLowerCase() ?? '';
  const hasRejectedOn = params.mode === INTERVENTION_MODE.TECHNICIAN || params.mode === INTERVENTION_MODE.PARTS;
  const isSales = params.mode === INTERVENTION_MODE.SALES;
  const { reducer: groupByReducer = [], sort = [] } = getGroupByFilters(params.dateField, groupBy);
  const reducer = [
    'sentOn,count',
    'acceptedOn,count',
    'acceptedManually,count',
    'acceptedFormally,count',
    'count,count',
    'readOn,count',
    'rejectedOn,count',
    'answered,sum',
    ...groupByReducer,
    ...(hasRejectedOn ? ['rejectedOn,count'] : []),
    ...(isSales ? ['opinion,count'] : []),
  ];

  return {
    type: types.FETCH_STATISTICS_TIMELINE_STATUS_STATS_REQUEST,
    payload: {
      request: {
        method: 'GET',
        url: `${STATISTICS_URL[params.mode]}/reduce`,
        params: {
          ...params,
          size: MAX_RECORD,
          reducer,
          ...(params.sentFrom ? { exist: 'sentOn' } : {}),
          sort,
          status: STATS_STATUS,
          statusBeforeClose: STATS_STATUS_BEFORE_CLOSE,
        },
      },
    },
    // @ts-ignore
    params,
    meta,
  };
};

/**
 * @param {import('modules/global/generic.types').PageContent<import('modules/statistics/types').TimelineStatusReducer>} data
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchTimeLineStatusStatsSuccessAction}
 */
export const fetchTimeLineStatusStatsSuccess = (data, meta = {}) => ({
  type: types.FETCH_STATISTICS_TIMELINE_STATUS_STATS_SUCCESS,
  payload: data,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchTimeLineStatusStatsFailureAction}
 */
export const fetchTimeLineStatusStatsFailure = (error, meta = {}) => ({
  type: types.FETCH_STATISTICS_TIMELINE_STATUS_STATS_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {import('modules/statistics/types').ReducerParams} request
 * @returns {Array<string>}
 */
const getDealerModulesReducers = ({ mode = INTERVENTION_MODE.TECHNICIAN, modules = [] }) =>
  (mode !== PARTS ? [
    `${camelCase(`mode_${mode}`)},count`,
    ...modules.map(module => `${camelCase(`module_${module}`)},count`),
  ] : ['mode,count']);

/**
 * @param {{ displayAll: boolean } & import('modules/statistics/types').ReducerParams} params
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchAllDealersStatsRequestAction}
 */
export const fetchAllDealersStatsRequest = ({ displayAll, ...params }, meta = {}) => {
  const url = `${STATISTICS_URL[params.mode]}/reduce`;

  const reducer = getDealerModulesReducers(params);

  const exist = !displayAll || params.sentFrom ? ['sentOn'] : [];

  return {
    type: types.FETCH_STATISTICS_ALL_DEALERS_REQUEST,
    payload: {
      request: {
        method: 'GET',
        url,
        params: {
          ...params,
          reducer: [
            ...reducer,
            'conversionRatio,avg',
            'amountRatio,func',
            'accepted,sum',
            'acceptedByDealer,sum',
            'proposed,sum',
            'sent,sum',
            'hasAcceptedAdvice,sum',
            'checklistProposed,count',
            'acceptedOn,count',
            'hasVideo,sum',
          ],
          status: STATS_STATUS,
          statusBeforeClose: STATS_STATUS_BEFORE_CLOSE,
          exist,
        },
      },
    },
    meta,
  };
};

/**
 * @param {import('modules/global/generic.types').PageContent<import('modules/statistics/types').AllDealersStatisticsReducer>} data
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchAllDealersStatsSuccessAction}
 */
export const fetchAllDealersStatsSuccess = (data, meta = {}) => ({
  type: types.FETCH_STATISTICS_ALL_DEALERS_SUCCESS,
  payload: data,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchAllDealersStatsFailureAction}
 */
export const fetchAllDealersStatsFailure = (error, meta) => ({
  type: types.FETCH_STATISTICS_ALL_DEALERS_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {{ displayAll: boolean } & import('modules/statistics/types').ReducerParams} params
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchDealersStatsRequestAction}
 */
export const fetchDealersStatsRequest = (params, meta = {}) => {
  const {
    payload: { request },
  } = fetchAllDealersStatsRequest(params);

  return {
    type: types.FETCH_STATISTICS_DEALERS_REQUEST,
    payload: {
      request: {
        ...request,
        params: {
          sort: ['dealerId,desc', 'dealerName,desc'],
          ...request.params,
          reducer: ['dealerId,none', 'dealerName,none', 'country,none', ...request.params.reducer],
          paginationType: PaginationType.SLICE,
        },
      },
    },
    meta,
  };
};

/**
 * @param {import('modules/global/generic.types').PageContent<import('modules/statistics/types').DealersStatisticsReducer>} data
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchDealersStatsSuccessAction}
 */
export const fetchDealersStatsSuccess = (data, meta = {}) => ({
  type: types.FETCH_STATISTICS_DEALERS_SUCCESS,
  payload: data,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchDealersStatsFailureAction}
 */
export const fetchDealersStatsFailure = (error, meta = {}) => ({
  type: types.FETCH_STATISTICS_DEALERS_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {{ displayAll: boolean } & import('modules/statistics/types').ReducerParams} params
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchDealersCountriesRequestAction}
 */
export const fetchDealersCountriesRequest = (params, meta = {}) => {
  const {
    payload: { request },
  } = fetchAllDealersStatsRequest(params);

  return {
    type: types.FETCH_DEALERS_COUNTRIES_REQUEST,
    payload: {
      request: {
        ...request,
        method: 'GET',
        params: {
          ...request.params,
          reducer: ['dealerId,count', 'country,none'],
          sort: 'dealerId,desc',
        },
      },
    },
    meta,
  };
};

/**
 * @param {import('modules/global/generic.types').PageContent<import('modules/statistics/types').DealersCountriesReducer>} data
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchDealersCountriesSuccessAction}
 */
export const fetchDealersCountriesSuccess = (data, meta = {}) => ({
  type: types.FETCH_DEALERS_COUNTRIES_SUCCESS,
  payload: data,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchDealersCountriesFailureAction}
 */
export const fetchDealersCountriesFailure = (error, meta = {}) => ({
  type: types.FETCH_DEALERS_COUNTRIES_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {import('modules/statistics/types').ReducerParams} params
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchCustomerOpinionStatsRequestAction}
 */
export const fetchCustomerOpinionStatsRequest = (params, meta = {}) => ({
  type: types.FETCH_CUSTOMER_OPINION_STATS_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: INTERVENTIONS_STATISTICS_REDUCE_URL,
      params: {
        ...params,
        reducer: ['opinion,none', 'count,count'],
        ...(params.sentFrom ? { exist: 'sentOn' } : {}),
      },
    },
  },
  meta,
});

/**
 * @param {import('modules/global/generic.types').PageContent<import('modules/statistics/types').CustomerOpinionReducer>} data
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchCustomerOpinionStatsSuccessAction}
 */
export const fetchCustomerOpinionStatsSuccess = (data, meta = {}) => ({
  type: types.FETCH_CUSTOMER_OPINION_STATS_SUCCESS,
  payload: data,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchCustomerOpinionStatsFailureAction}
 */
export const fetchCustomerOpinionStatsFailure = (error, meta = {}) => ({
  type: types.FETCH_CUSTOMER_OPINION_STATS_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {import('modules/statistics/types').ReducerParams} params
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchTopContributorsStatsRequestAction}
 */
export const fetchTopContributorsStatsRequest = (params, meta = {}) => ({
  type: types.FETCH_STATISTICS_TOP_CONTRIBUTORS_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: TOP_CONTRIBUTORS_STATISTICS_URL,
      params: {
        ...params,
        status: STATS_STATUS,
        statusBeforeClose: STATS_STATUS_BEFORE_CLOSE,
        ...(params.sentFrom ? { exist: 'sentOn' } : {}),
      },
    },
  },
  meta,
});

/**
 * @param {import('modules/global/generic.types').PageContent<import('modules/statistics/types').TopContributorsReducer>} data
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchTopContributorsStatsSuccessAction}
 */
export const fetchTopContributorsStatsSuccess = (data, meta = {}) => ({
  type: types.FETCH_STATISTICS_TOP_CONTRIBUTORS_SUCCESS,
  payload: data,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchTopContributorsStatsFailureAction}
 */
export const fetchTopContributorsStatsFailure = (error, meta = {}) => ({
  type: types.FETCH_STATISTICS_TOP_CONTRIBUTORS_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {import('modules/statistics/types').ReducerParams} params
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchAnswerTimeRequestAction}
 */
export const fetchAnswerTimeRequest = (params, meta = {}) => ({
  type: types.FETCH_STATISTICS_ANSWER_TIME_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: `${STATISTICS_URL[params.mode]}/reduce`,
      params: {
        ...params,
        reducer: ['responseTime,avg', 'id,count'],
        status: [ANSWERED, CLOSED],
        statusBeforeClose: [ANSWERED],
        ...(params.sentFrom ? { exist: 'sentOn' } : {}),
      },
    },
  },
  meta,
});

/**
 * @param {import('modules/global/generic.types').PageContent<import('modules/statistics/types').AnswerTimeReducer>} data
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchAnswerTimeSuccessAction}
 */
export const fetchAnswerTimeSuccess = (data, meta = {}) => ({
  type: types.FETCH_STATISTICS_ANSWER_TIME_SUCCESS,
  payload: data,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchAnswerTimeFailureAction}
 */
export const fetchAnswerTimeFailure = (error, meta = {}) => ({
  type: types.FETCH_STATISTICS_ANSWER_TIME_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {import('modules/statistics/types').ReducerParams} params
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchInterventionCountRequestAction}
 */
export const fetchInterventionCountRequest = (params, meta = {}) => ({
  type: types.FETCH_STATISTICS_INTERVENTION_COUNT_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: `${STATISTICS_URL[params.mode]}/reduce`,
      params: {
        ...params,
        reducer: ['id,count', 'checklistProposed,count'],
        status: STATS_STATUS,
        statusBeforeClose: STATS_STATUS_BEFORE_CLOSE,
        ...(params.sentFrom ? { exist: 'sentOn' } : {}),
      },
    },
  },
  meta,
});

/**
 * @param {import('modules/statistics/types').InterventionCountReducer} data
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchInterventionCountSuccessAction}
 */
export const fetchInterventionCountSuccess = (data, meta) => ({
  type: types.FETCH_STATISTICS_INTERVENTION_COUNT_SUCCESS,
  payload: data,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchInterventionCountFailureAction}
 */
export const fetchInterventionCountFailure = (error, meta = {}) => ({
  type: types.FETCH_STATISTICS_INTERVENTION_COUNT_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {import('modules/statistics/types').ReducerParams} params
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchRevenueStatsRequestAction}
 */
export const fetchRevenueStatsRequest = (params, meta = {}) => {
  const isTechnician = params.mode === INTERVENTION_MODE.TECHNICIAN;

  /**
   * @param {Object} customParams
   * @returns {import('modules/global/generic.types').Request<'GET', import('modules/statistics/types').ReducerParams>}
   */
  const getRequest = customParams => {
    let exist = [...(customParams.exist ?? [])];
    if (params.sentFrom) {
      exist = [...(exist ?? []), 'sentOn'];
    }

    return {
      method: 'GET',
      url: `${STATISTICS_URL[params.mode]}/reduce`,
      params: {
        ...params,
        status: STATS_STATUS,
        statusBeforeClose: STATS_STATUS_BEFORE_CLOSE,
        ...customParams,
        exist
      },
    };
  };

  return {
    type: types.FETCH_STATISTICS_REVENUE_STATS_REQUEST,
    payload: {
      requests: [
        getRequest({
          reducer: ['accepted,sum', 'proposed,sum', 'amountRatio,func'],
          ...(isTechnician ? { exist: ['checklistProposed'] } : {}),
        }),
        getRequest({
          reducer: ['accepted,avg'],
          ...(isTechnician ? { exist: ['checklistProposed', 'acceptedOn'] } : {}),
        }),
        getRequest({
          reducer: ['accepted,sum', 'proposed,sum', 'answered,none'],
        }),
      ],
    },
    meta,
  };
};

/**
 * @param {import('modules/statistics/types').RevenueReducer} data
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchRevenueStatsSuccessAction}
 */
export const fetchRevenueStatsSuccess = (data, meta = {}) => ({
  type: types.FETCH_STATISTICS_REVENUE_STATS_SUCCESS,
  payload: data,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchInterventionCountFailureAction}
 */
export const fetchRevenueStatsFailure = (error, meta = {}) => ({
  type: types.FETCH_STATISTICS_REVENUE_STATS_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {import('modules/statistics/types').ReducerParams} params
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchRevenueByBrandStatsRequestAction}
 */
export const fetchRevenueByBrandStatsRequest = (params, meta = {}) => ({
  type: types.FETCH_STATISTICS_REVENUE_BY_BRAND_STATS_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: `${STATISTICS_URL[params.mode]}/reduce`,
      params: {
        ...params,
        reducer: ['accepted,sum', 'proposed,sum', 'brandId,none', 'brandName,none'],
        ...(params.sentFrom ? { exist: 'sentOn' } : {}),
        status: STATS_STATUS,
        statusBeforeClose: STATS_STATUS_BEFORE_CLOSE
      },
    },
  },
  meta,
});

/**
 * @param {import('modules/global/generic.types').PageContent<import('modules/statistics/types').RevenueByBrandReducer>} data
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchRevenueByBrandStatsSuccessAction}
 */
export const fetchRevenueByBrandStatsSuccess = (data, meta) => ({
  type: types.FETCH_STATISTICS_REVENUE_BY_BRAND_STATS_SUCCESS,
  payload: data,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchRevenueByBrandStatsFailureAction}
 */
export const fetchRevenueByBrandStatsFailure = (error, meta = {}) => ({
  type: types.FETCH_STATISTICS_REVENUE_BY_BRAND_STATS_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {{groupBy: import('modules/statistics/groupBy').GroupBy} & Partial<Record<import('modules/statistics/groupBy').GroupBy, number>> & import('modules/statistics/types').ReducerParams} params
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchRevenueByPeriodStatsRequestAction}
 */
export const fetchRevenueByPeriodStatsRequest = (
  {
    groupBy: groupedBy,
    from: _from,
    to: _to,
    sentFrom: _sentFrom,
    sentTo: _sentTo,
    startDateFrom: _startDateFrom,
    startDateTo: _startDateTo,
    year: _year,
    ...params
  },
  meta = {}
) => {
  const groupBy = groupedBy.toLowerCase();
  const isYear = groupBy === YEAR;
  const isDay = groupBy === DAY;

  return {
    type: types.FETCH_STATISTICS_REVENUE_BY_PERIOD_STATS_REQUEST,
    payload: {
      request: {
        method: 'GET',
        url: `${STATISTICS_URL[params.mode]}/reduce`,
        // @ts-ignore
        params: {
          ...params,
          reducer: ['accepted,sum', `${groupBy},none`, ...(isDay ? ['month,none'] : []), ...(isYear ? [] : ['year,none'])],
          ...(params.dateField === DATE_FIELDS.SEND ? { exist: 'sentOn' } : {}),
          sort: ['year,asc']
        },
      },
    },
    meta,
  };
};

/**
 * @param {import('modules/global/generic.types').PageContent<import('modules/statistics/types').RevenueByPeriodReducer>} data
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchRevenueByPeriodStatsSuccessAction}
 */
export const fetchRevenueByPeriodStatsSuccess = (data, meta = {}) => ({
  type: types.FETCH_STATISTICS_REVENUE_BY_PERIOD_STATS_SUCCESS,
  payload: data,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchRevenueByPeriodStatsFailureAction}
 */
export const fetchRevenueByPeriodStatsFailure = (error, meta = {}) => ({
  type: types.FETCH_STATISTICS_REVENUE_BY_PERIOD_STATS_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {string} period
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').SetPeriodAction}
 */
export const setPeriod = (period, meta = {}) => ({
  type: types.SET_DEALER_STATISTICS_PERIOD,
  payload: period,
  meta,
});

/**
 * @param {{brandIds : []} & import('modules/statistics/types').ReducerParams} params
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchDealersStatsBrandsRequestAction}
 */
export const fetchDealersStatsBrandsRequest = (params, meta = {}) => {
  const hasBrandIds = params?.brandIds?.length > 0;
  let url = `${STATISTICS_URL[params.mode]}/reduce`;
  let requestParameters = {
    ...params,
    status: STATS_STATUS,
    sort: ['brandName,asc'],
    modules: getStatsModules(),
    reducer: ['brandId,none', 'brandName,none'],
    ...(params.sentFrom ? { exist: 'sentOn' } : {}),
    statusBeforeClose: STATS_STATUS_BEFORE_CLOSE
  };

  if (hasBrandIds) {
    const { brandIds: ids } = params;
    url = BRANDS_FILTER_URL;
    requestParameters = {
      // @ts-ignore
      fields: 'id,name',
      ids,
    };
  }

  if (!params.groupId) {
    url = BRANDS_FILTER_URL;
    requestParameters = {};
  }

  return {
    type: types.FETCH_STATISTICS_INTERVENTION_BRANDS_REQUEST,
    payload: {
      request: {
        method: 'GET',
        url,
        // @ts-ignore
        params: {
          ...requestParameters,
          size: MAX_RECORD,
        },
      },
    },
    meta,
  };
};

/**
 * @param {Array<import('modules/statistics/types').DealersStatsBrandsReducer>} brands
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchDealersStatsBrandsSuccessAction}
 */
export const fetchDealersStatsBrandsSuccess = (brands, meta = {}) => ({
  type: types.FETCH_STATISTICS_INTERVENTION_BRANDS_SUCCESS,
  payload: {
    brands,
  },
  meta,
});

/**
 * @param {string} message
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchDealersStatsBrandsFailureAction}
 */
export const fetchDealersStatsBrandsFailure = (message, meta = {}) => ({
  type: types.FETCH_STATISTICS_INTERVENTION_BRANDS_FAILURE,
  payload: message,
  error: true,
  meta,
});

/**
 * @param {{[filterField: string] : any}} payload
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').SetDealersStatsFiltersAction}
 */
export const setDealersStatsFilters = (payload, meta = {}) => ({
  type: types.SET_DEALER_STATISTICS_FILTERS,
  payload,
  meta,
});

/**
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').DetailsIsReadyAction}
 */
export const detailsIsReady = (meta = {}) => ({
  type: types.STATISTICS_DEALER_DETAILS_READY,
  meta,
});

/** @type {Object<string, Partial<import('modules/statistics/types').StatisticExportItem>>} */
const RAW_EXPORT_PARAMETERS_BY_FIELD = {
  interventions: { adviceStatistics: false },
  works: { adviceStatistics: true },
  videos: { type: ExportTypeEnum.RAW_MEDIA_EXPORT, mediaTypes: [MediaTypeEnum.VIDEO] },
};

/**
 * @param {import('modules/statistics/types').ExportStatisticsData & import('modules/statistics/types').ExportReducerParams} params
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').ExportStatisticsAction}
 */
export const exportStatistics = ({ raw, excel, url, ...params }, meta = {}) => {
  /** @type {Array<import('modules/statistics/types').StatisticExportItem>} */
  const exports = [];
  if (excel) {
    exports.push({ type: ExportTypeEnum.BO_EXPORT });
  }

  Object.entries(raw).forEach(([key, value]) => {
    if (key !== 'anonymize' && value) {
      exports.push({
        type: ExportTypeEnum.RAW_EXPORT,
        anonymize: raw.anonymize,
        ...RAW_EXPORT_PARAMETERS_BY_FIELD[key],
      });
    }
  });

  return {
    type: types.STATISTICS_DEALER_EXPORT,
    payload: {
      request: {
        method: 'POST',
        url: `${url}/export`,
        data: {
          ...params,
          status: STATS_STATUS,
          exports,
        },
      },
    },
    meta,
  };
};

/**
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').ExportStatisticsSuccessAction}
 */
export const exportStatisticsSuccess = (meta = {}) => ({
  type: types.STATISTICS_DEALER_EXPORT_SUCCESS,
  meta,
});

/**
 * @param {string} message
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').ExportStatisticsFailureAction}
 */
export const exportStatisticsFailure = (message, meta = {}) => ({
  type: types.STATISTICS_DEALER_EXPORT_FAILURE,
  payload: message,
  error: true,
  meta,
});

/**
 * @param {boolean} visible
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').SetExportModalVisibilityAction}
 */
export const setExportModalVisibility = (visible, meta = {}) => ({
  type: types.STATISTICS_DEALER_EXPORT_MODAL_VISIBILITY,
  payload: visible,
  meta,
});

/**
 * @param {import('modules/statistics/types').ReducerParams & { groupBy: string }} payload
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchChatStatisticsRequestAction}
 */
export const fetchChatStatisticsRequest = ({ mode, dealerId, groupBy = DAY, ...params }, meta = {}) => {
  const { reducer: groupByReducer, sort } = getGroupByFilters(DATE_FIELDS.CREATED_ON, groupBy);

  return {
    type: actionTypes.FETCH_CHAT_STATISTICS_REQUEST,
    payload: {
      requests: [
        {
          url: `${STATISTICS_URL[mode]}/reduce`,
          method: 'GET',
          // @ts-ignore
          params: {
            ...params,
            dealerId,
            modes: mode,
            size: MAX_RECORD,
            // @ts-ignore
            reducer: ['hasInternalMessages,sum', 'hasCustomerMessages,sum', 'sentOn,count', ...groupByReducer],
            sort
          },
        },
        {
          url: `${CHAT_STATISTICS_URL_BY_MODE[mode]}/reduce`,
          method: 'GET',
          // @ts-ignore
          params: {
            ...params,
            dealerId,
            modes: mode,
            size: MAX_RECORD,
            fromCustomer: true,
            reducer: ['channel,none', 'id,count'],
          },
        },
        {
          url: `${CHAT_STATISTICS_URL_BY_MODE[mode]}/reduce`,
          method: 'GET',
          // @ts-ignore
          params: {
            ...params,
            dealerId,
            modes: mode,
            size: MAX_RECORD,
            fromCustomer: true,
            sort: ['time,asc'],
            reducer: ['time,none', 'id,count'],
          },
        },
        {
          url: `${STATISTICS_URL[mode]}/reduce`,
          method: 'GET',
          // @ts-ignore
          params: {
            ...params,
            dealerId,
            modes: mode,
            reducer: ['messageCount,avg']
          },
        },
        {
          url: `${CHAT_STATISTICS_URL_BY_MODE[mode]}/reduce`,
          method: 'GET',
          // @ts-ignore
          params: {
            ...params,
            dealerId,
            modes: mode,
            size: MAX_RECORD,
            fromCustomer: false,
            exist: ['responseTime'],
            reducer: ['responseTime,avg'],
          },
        },
        {
          url: `${CHAT_STATISTICS_URL_BY_MODE[mode]}/reduce`,
          method: 'GET',
          params: {
            ...params,
            dealerId,
            modes: mode,
            size: MAX_RECORD,
            // @ts-ignore
            fromUser: true,
            reducer: ['visibility,none', 'id,count'],
          },
        },

        {
          url: `${CHAT_STATISTICS_URL_BY_MODE[mode]}/reduce`,
          method: 'GET',
          params: {
            ...params,
            dealerId,
            modes: mode,
            size: MAX_RECORD,
            reducer: [
              'publicMedia,sum'
            ],
          }
        },
        {
          url: `${CHAT_STATISTICS_URL_BY_MODE[mode]}/reduce`,
          method: 'GET',
          params: {
            ...params,
            dealerId,
            modes: mode,
            size: MAX_RECORD,
            reducer: [
              'internalMedia,sum'
            ],
          }
        },
        {
          url: `${CHAT_STATISTICS_URL_BY_MODE[mode]}/reduce`,
          method: 'GET',
          params: {
            ...params,
            dealerId,
            modes: mode,
            size: MAX_RECORD,
            reducer: [
              'publicMedia,avg'
            ],
          }
        },
        {
          url: `${STATISTICS_URL[mode]}/reduce`,
          method: 'GET',
          params: {
            ...params,
            dealerId,
            modes: mode,
            size: MAX_RECORD,
            reducer: [
              'serviceAdvisorMessagePhotosCount,sum',
              'serviceAdvisorMessageVideosCount,sum',
              'serviceAdvisorMessageDocumentsCount,sum',
            ],
          }
        },
        {
          url: `${STATISTICS_URL[mode]}/reduce`,
          method: 'GET',
          params: {
            ...params,
            dealerId,
            modes: mode,
            size: MAX_RECORD,
            reducer: [
              'customerMessagePhotosCount,sum',
              'customerMessageVideosCount,sum',
              'customerMessageDocumentsCount,sum',
            ],
          }
        },
      ],
    },
    meta,
  };
};

/**
 * @param {import('./reducer/data/chat/data').ChatStatsDataReducer} payload
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchChatStatisticsSuccessAction}
 */
export const fetchChatStatisticsSuccess = (
  payload,
  meta = {}
) => ({
  type: actionTypes.FETCH_CHAT_STATISTICS_SUCCESS,
  payload,
  meta,
});

/**
 * @param {string} message
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchChatStatisticsFailureAction}
 */
export const fetchChatStatisticsFailure = (message, meta = {}) => ({
  type: actionTypes.FETCH_CHAT_STATISTICS_FAILURE,
  payload: message,
  error: true,
  meta,
});

/**
 * @param {import('modules/statistics/types').ReducerParams} params
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchSelfServiceStatisticsRequestAction}
 */
export const fetchDashboardSelfServiceStats = (params, meta = {}) => ({
  type: actionTypes.FETCH_DASHBOARD_SELF_SERVICE_STATS,
  payload: {
    request: {
      method: 'GET',
      url: SELF_SERVICE_STATISTICS_REDUCE_URL,
      params: {
        ...params,
        reducer: [
          'id,count',
          'country,none',
          'dealerId,none',
          'checkInSent,sum',
          'dealerName,none',
          'checkOutSent,sum',
          'homeCheckInCompleted,sum',
          'homeCheckOutCompleted,sum',
          'kioskCheckInCompleted,sum',
          'kioskCheckOutCompleted,sum',
          'manualCheckOutCompleted,sum',
          'acceptedExtraOrProactive,sum',
          'proposedExtraOrProactive,sum',
          'onSiteOrKioskCheckInCompleted,sum',
          'onSiteOrKioskCheckOutCompleted,sum',
        ],
        paginationType: PaginationType.SLICE,
      },
    },
  },
  meta,
});

/**
 * @param {import('modules/global/generic.types').PageContent<import('./reducer/data/selfServiceDashboard/data').SelfServiceStatsDataReducer>} payload
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchSelfServiceStatisticsSuccessAction}
 */
export const fetchDashboardSelfServiceStatsSuccess = (payload, meta = {}) => ({
  type: actionTypes.FETCH_DASHBOARD_SELF_SERVICE_STATS_SUCCESS,
  payload,
  meta,
});

/**
 * @param {Error} error
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchSelfServiceStatisticsFailureAction}
 */
export const fetchDashboardSelfServiceStatsFailure = (error, meta = {}) => ({
  type: actionTypes.FETCH_DASHBOARD_SELF_SERVICE_STATS_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {import('modules/statistics/types').ReducerParams & {groupBy?: string}} params
 * @param {import('modules/statistics/types').SelfServiceRequestType[]} includedRequestTypes
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchDetailsSelfServiceStatisticsRequestAction}
 */
export const fetchDetailsSelfServiceStats = (
  params,
  includedRequestTypes = Object.keys(SELF_SERVICE_STATS_REQUEST_TYPES),
  meta = {}
) => ({
  type: actionTypes.FETCH_DETAILS_SELF_SERVICE_STATS,
  payload: {
    includedRequestTypes,
    requests: includedRequestTypes.reduce(
      (acc, type) => ({ ...acc, [type]: DETAILS_SELF_SERVICE_STATS_BY_TYPES[type](params) }),
      {}
    ),
  },
  meta,
});

/**
 * @param {import('modules/statistics/types').FetchDetailsSelfServiceStatisticsSuccessActionPayload} payload
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchDetailsSelfServiceStatisticsSuccessAction}
 */
export const fetchDetailsSelfServiceStatsSuccess = (payload, meta = {}) => ({
  type: actionTypes.FETCH_DETAILS_SELF_SERVICE_STATS_SUCCESS,
  payload,
  meta,
});

/**
 * @param {Error} error
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchDetailsSelfServiceStatisticsFailureAction}
 */
export const fetchDetailsSelfServiceStatsFailure = (error, meta = {}) => ({
  type: actionTypes.FETCH_DETAILS_SELF_SERVICE_STATS_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {Partial<import('modules/statistics/types').SelfServiceDetailsFilters>} filters
 * @param {{refreshRequestTypes: import('modules/statistics/types').SelfServiceRequestType[]}} [meta={}]
 * @returns {import('modules/statistics/types').SetDetailsStatsSelfServiceFiltersAction}
 */
export const setDetailsStatsSelfServiceFilters = (filters, meta = {}) => ({
  type: actionTypes.SET_DETAILS_STATS_SELF_SERVICE_FILTERS,
  payload: filters,
  meta,
});

export const setExtraFilters = (field, value, meta = {}) => ({
  type: actionTypes.SET_EXTRA_FILTER,
  payload: { field, value },
  meta,
});

export const setProactiveWorksFilters = (field, value, meta = {}) => ({
  type: actionTypes.SET_PROACTIVE_WORKS_FILTER,
  payload: { field, value },
  meta,
});

export const setSelfServiceTab = (payload, meta = {}) => ({
  type: actionTypes.SET_SELECTED_SELF_SERVICE_TAB,
  payload,
  meta,
});

export const fetchExtraDetailsStats = (params, meta = {}) => ({
  type: actionTypes.FETCH_EXTRA_DETAILS_STATS,
  payload: {
    request: {
      method: 'GET',
      url: SELF_SERVICE_ADVICE_STATISTICS_REDUCE_URL,
      params: {
        ...params,
        reducer: [
          'id,count',
          'isSent,count',
          'isCompleted,count',
          'adviceName,none',
          'statusPending,sum',
          'statusRefused,sum',
          'statusAccepted,sum',
          'acceptedAmount,sum',
          'adviceNameHash,none',
          'isSentOrCompleted,count',
          'averageAcceptedAmount,func',
        ],
      },
    },
  },
  meta,
});

export const fetchExtraDetailsStatsSuccess = (payload, meta = {}) => ({
  type: actionTypes.FETCH_EXTRA_DETAILS_STATS_SUCCESS,
  payload,
  meta,
});

export const fetchExtraDetailsStatsFailure = (error, meta = {}) => ({
  type: actionTypes.FETCH_EXTRA_DETAILS_STATS_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const fetchProactiveWorksDetailsStats = (params, meta = {}) => ({
  type: actionTypes.FETCH_PROACTIVE_WORKS_DETAILS_STATS,
  payload: {
    request: {
      method: 'GET',
      url: SELF_SERVICE_PROACTIVE_WORKS_STATISTICS_REDUCE_URL,
      params: {
        ...params,
        reducer: [
          'id,count',
          'isSent,count',
          'isCompleted,count',
          'adviceName,none',
          'statusPending,sum',
          'statusRefused,sum',
          'statusAccepted,sum',
          'acceptedAmount,sum',
          'adviceNameHash,none',
          'isSentOrCompleted,count',
          'averageAcceptedAmount,func',
        ],
      },
    },
  },
  meta: {
    ...meta,
    '@app-request': true
  },
});

/**
 * @param {import('modules/statistics/types').ReducerParams} params
 * @param {Object} [meta={}]
 * @returns {import('modules/statistics/types').FetchTopContributorsStatsRequestAction}
 */
export const fetchSelfServiceTopContributorsStatsRequest = (params, meta = {}) => ({
  type: types.FETCH_SELF_SERVICE_STATISTICS_TOP_CONTRIBUTORS_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: SELF_SERVICE_STATISTICS_TOP_CONTRIBUTORS_URL,
      params: {
        ...params,
      },
    },
  },
  meta: { ...meta, '@app-request': true },
});

export const setSelfServiceTopContributorsFilter = (field, value, meta = {}) => ({
  type: actionTypes.SET_SELF_SERVICE_TOP_CONTRIBUTORS_FILTER,
  payload: { field, value },
  meta,
});
